import React, { useMemo, useState } from "react";
import { api, toastError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { FileDropzone, SelectedFile } from "../components/FileDropzone";
import { toast } from "sonner";

/**
 * Step 2 — Upload CSV.
 *
 * Rendered while `job.status === "RECEIVED"`. After a successful upload, the
 * user clicks Analyze which drives the backend RECEIVED -> ANALYZING -> MAPPED
 * transition; the wizard shell then advances to Step 3 automatically because
 * the step is derived from `job.status`.
 *
 * If the job already carries `job.file.filename`, we render a "file attached"
 * card + an Analyze CTA so a returning user can pick up mid-flow.
 */
export function Step2Upload({
  jobId,
  file,
  onJobUpdated,
}: {
  jobId: string;
  file?: { filename?: string; byte_size?: number } | null;
  onJobUpdated: () => void;
}) {
  const [selected, setSelected] = useState<SelectedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [jobId]);

  const hasAttachedFile = !!file?.filename;

  const doUpload = async () => {
    if (!selected) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", selected.file);
      await api.post(`/import/jobs/${jobId}/upload`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Idempotency-Key": idempotencyKey,
        },
      });
      toast.success("File uploaded");
      setSelected(null);
      onJobUpdated();
    } catch (e) {
      toastError(e, "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const doAnalyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/import/jobs/${jobId}/analyze`);
      // The job transitions RECEIVED -> ANALYZING -> MAPPED synchronously.
      // The wizard shell re-derives the current step from job.status.
      onJobUpdated();
    } catch (e) {
      toastError(e, "Could not analyze the file.");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-2-upload">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Upload your CSV</h2>
        <p className="text-sm text-slate-500 mt-1">
          Drop your Contacts CSV below. You can replace the file until you continue to mapping.
        </p>
      </div>

      {hasAttachedFile && !selected && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"
          data-testid="attached-file-card"
        >
          <div className="font-medium text-emerald-900">File attached</div>
          <div className="text-emerald-800/80 mt-0.5">
            <span className="font-mono break-all">{file?.filename}</span>
            {typeof file?.byte_size === "number" && (
              <span> · {(file.byte_size / 1024).toFixed(1)} KB</span>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              onClick={doAnalyze}
              disabled={analyzing}
              data-testid="analyze-btn"
              className="rounded-full"
            >
              {analyzing ? "Analyzing…" : "Continue to mapping"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelected(null)}
              disabled={analyzing}
              className="rounded-full"
              data-testid="replace-file-btn"
            >
              Replace file
            </Button>
          </div>
        </div>
      )}

      <FileDropzone
        selected={selected}
        onSelect={setSelected}
        onClear={() => setSelected(null)}
        busy={uploading}
      />

      {selected && (
        <div className="flex gap-2">
          <Button
            onClick={doUpload}
            disabled={uploading}
            data-testid="upload-btn"
            className="rounded-full"
          >
            {uploading ? "Uploading…" : hasAttachedFile ? "Replace attached file" : "Upload"}
          </Button>
        </div>
      )}
    </div>
  );
}
